import { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import { useSession } from 'next-auth/react';

/**
 * Layout for the public marketing pages (`/`, `/pricing`, `/privacy`, `/terms`, `/contact`).
 *
 * Mirrors the structure of the old Flask app's `website/base.html` so URL shapes
 * and visual layout stay consistent through the cutover. Replaces Alpine.js
 * mobile menu state with React useState.
 *
 * @param {object} props
 * @param {string} props.title              Page title for <head>.
 * @param {string} [props.description]      Optional meta description.
 * @param {string} [props.canonical]        Optional canonical URL path (e.g. "/pricing").
 * @param {React.ReactNode} props.children  Page body.
 */
export default function MarketingLayout({ title, description, canonical, children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { status } = useSession();
  const signedIn = status === 'authenticated';

  return (
    <>
      <Head>
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
        {canonical && <link rel="canonical" href={`https://10xplannerapp.com${canonical}`} />}
      </Head>

      <nav className="bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
          <div className="border-b border-gray-700">
            <div className="flex items-center justify-between h-16 px-4 sm:px-0">
              <div className="flex items-center">
                <Link href="/" className="flex items-center flex-shrink-0">
                  <Image
                    src="/images/logo-icon.png"
                    alt="10X Planner"
                    width={32}
                    height={32}
                    className="h-8 w-auto"
                  />
                  <h2 className="text-xl font-bold leading-tight text-gray-100 ml-2">10X Planner</h2>
                </Link>
              </div>
              <div className="hidden md:flex items-center justify-end space-x-8 md:flex-1 lg:w-0">
                <Link
                  href="/pricing"
                  className="whitespace-nowrap text-base leading-6 font-medium text-gray-100 hover:text-gray-300 focus:outline-none"
                >
                  Pricing
                </Link>
                {signedIn ? (
                  <span className="inline-flex rounded-md shadow-sm">
                    <Link
                      href="/planner"
                      className="whitespace-nowrap inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-800 hover:bg-blue-900"
                    >
                      Go to planner
                    </Link>
                  </span>
                ) : (
                  <>
                    <Link
                      href="/login"
                      className="whitespace-nowrap text-base leading-6 font-medium text-gray-100 hover:text-gray-300 focus:outline-none"
                    >
                      Sign in
                    </Link>
                    <span className="inline-flex rounded-md shadow-sm">
                      <Link
                        href="/login"
                        className="whitespace-nowrap inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-800 hover:bg-blue-900"
                      >
                        Start free
                      </Link>
                    </span>
                  </>
                )}
              </div>
              <div className="-mr-2 flex md:hidden">
                <button
                  type="button"
                  onClick={() => setMobileOpen((open) => !open)}
                  aria-expanded={mobileOpen}
                  className="bg-gray-800 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                >
                  <span className="sr-only">Open main menu</span>
                  {mobileOpen ? (
                    <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div className="border-b border-gray-700 md:hidden">
            <div className="px-2 py-3 space-y-1 sm:px-3">
              <Link
                href="/pricing"
                className="text-gray-300 hover:bg-gray-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium"
              >
                Pricing
              </Link>
              {signedIn ? (
                <Link
                  href="/planner"
                  className="text-gray-300 hover:bg-gray-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium"
                >
                  Go to planner
                </Link>
              ) : (
                <>
                  <Link
                    href="/login"
                    className="text-gray-300 hover:bg-gray-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium"
                  >
                    Sign in
                  </Link>
                  <Link
                    href="/login"
                    className="text-gray-300 hover:bg-gray-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium"
                  >
                    Start free
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      <main className="bg-white">{children}</main>

      <footer className="bg-white">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 md:flex md:items-center md:justify-between lg:px-8">
          <div className="flex justify-center space-x-6 md:order-2">
            <Link href="/contact" className="text-gray-400 hover:text-gray-500">
              Contact
            </Link>
            <Link href="/terms" className="text-gray-400 hover:text-gray-500">
              Terms
            </Link>
            <Link href="/privacy" className="text-gray-400 hover:text-gray-500">
              Privacy
            </Link>
          </div>
          <div className="mt-8 md:mt-0 md:order-1">
            <p className="text-center text-base text-gray-400">
              &copy; {new Date().getFullYear()} 10X Planner App. All rights reserved.
            </p>
            <p className="text-center text-xs text-gray-400 mt-2 max-w-2xl mx-auto">
              10X Planner App is an independent product and is not affiliated with, endorsed by,
              or connected to Grant Cardone, Cardone Training Technologies, or any related
              entities. &ldquo;10X&rdquo; is used in its common descriptive sense meaning
              &ldquo;tenfold improvement.&rdquo;
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
